/**
 * `<SectionAndClipToolCard />` — WP-6 (3D model / BIM / 3D Tiles
 * ToolCard companion).
 *
 * Right-rail card surfacing section planes (X / Y / Z + custom plane
 * normal in MONO) and the axis-aligned clipping box (with
 * "tighten to selection" affordance). Sits beside
 * `<ThreeDModelToolCard />` whenever the active layer family is
 * `'bim'` or `'3dtiles'`.
 *
 * Spec references:
 *   - `docs/results/VIEWER-TOOLBAR-AND-GTWIN-FIRST-UX-MASTERPLAN.md`
 *     §7.6 Card 6 — section planes (X/Y/Z, oblique, multiple planes)
 *     and clipping box (axis-aligned, oblique).
 *   - `geosoft-platform-design-system/docs/DESIGN_POLICY.md` §5 —
 *     tokens only, single primary CTA per row, 40 px touch min,
 *     non-color cue per state.
 *   - Custom plane normals are displayed in MONO per
 *     `ICON_SYSTEM_PLAYBOOK.md` typography rules for technical
 *     identifiers.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type CSSProperties,
} from 'react';
import {
  Banner,
  Button,
  Icon,
  IconButton,
  tokens,
  type IconName,
} from '@/design';
import {
  TOOL_CARD_REQUEST_EVENT,
  type ToolCardRequestDetail,
} from '@/app/sceneSelection';
import { useViewerStore } from '@/state/useViewerStore';
import {
  clampClipPosition,
  formatNormalInput,
  parseNormalInput,
  DEFAULT_CLIP_POSITION,
  DEFAULT_CLIPPING_BOX,
  type ClipAxis,
  type ClippingBoxSpec,
  type SectionPlaneSpec,
} from '@/engines/giro3d/clipController';
import type { ModelBounds } from '@/engines/giro3d/cameraPresets';

// ---------------------------------------------------------------------------
// Adapter contract
// ---------------------------------------------------------------------------

export interface SectionAndClipCommandAdapter {
  setSectionPlane(spec: SectionPlaneSpec | null): boolean;
  setClippingBox(box: ClippingBoxSpec | null): boolean;
  getModelBounds(): ModelBounds | null;
  /** Optional selection-bounds reader powering "tighten to selection". */
  getSelectionBounds?(): ModelBounds | null;
}

// ---------------------------------------------------------------------------
// Component props
// ---------------------------------------------------------------------------

export interface SectionAndClipToolCardProps {
  adapter: SectionAndClipCommandAdapter | null;
  /** Force the card visible regardless of the selection contract. */
  forceVisible?: boolean;
  /** Optional close handler — when supplied the card surfaces an X button. */
  onClose?: () => void;
  'data-testid'?: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const AXIS_OPTIONS: ReadonlyArray<{
  readonly axis: ClipAxis;
  readonly label: string;
  readonly accessibleLabel: string;
}> = [
  { axis: 'x', label: 'X', accessibleLabel: 'Section plane along X axis' },
  { axis: 'y', label: 'Y', accessibleLabel: 'Section plane along Y axis' },
  { axis: 'z', label: 'Z', accessibleLabel: 'Section plane along Z axis' },
];

export function SectionAndClipToolCard({
  adapter,
  forceVisible = false,
  onClose,
  'data-testid': testId,
}: SectionAndClipToolCardProps) {
  const sceneSelectionFromHook = useViewerStore((s) => s.sceneSelection);
  const liveSceneSelection = useViewerStore.getState().sceneSelection;
  const sceneSelection =
    sceneSelectionFromHook.kind === 'cleared'
      ? liveSceneSelection
      : sceneSelectionFromHook;

  const [eventToolCardKind, setEventToolCardKind] = useState<string | null>(null);
  const [activeAxis, setActiveAxis] = useState<ClipAxis | null>(null);
  const [position, setPosition] = useState<number>(DEFAULT_CLIP_POSITION);
  const [flip, setFlip] = useState<boolean>(false);
  const [normalInput, setNormalInput] = useState<string>('');
  const [normalParseError, setNormalParseError] = useState<boolean>(false);
  const [clipBox, setClipBox] = useState<ClippingBoxSpec | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<ToolCardRequestDetail>).detail;
      if (!detail) return;
      setEventToolCardKind(detail.toolCardKind);
    };
    window.addEventListener(TOOL_CARD_REQUEST_EVENT, handler);
    return () => window.removeEventListener(TOOL_CARD_REQUEST_EVENT, handler);
  }, []);

  const selectionActivates =
    sceneSelection.toolCardKind === 'bim' ||
    sceneSelection.toolCardKind === '3dtiles' ||
    sceneSelection.layerKind === 'bim' ||
    sceneSelection.layerKind === 'glb' ||
    sceneSelection.layerKind === '3dtiles' ||
    sceneSelection.layerKind === 'cityjson-3dtiles';
  const eventActivates =
    eventToolCardKind === 'bim' || eventToolCardKind === '3dtiles';
  const visible = forceVisible || selectionActivates || eventActivates;

  const customNormal = useMemo(() => {
    if (!normalInput.trim()) return null;
    return parseNormalInput(normalInput);
  }, [normalInput]);

  const applyPlane = useCallback(
    (
      spec: SectionPlaneSpec | null,
    ) => {
      adapter?.setSectionPlane(spec);
    },
    [adapter],
  );

  const handleAxisToggle = useCallback(
    (axis: ClipAxis) => {
      if (activeAxis === axis) {
        setActiveAxis(null);
        applyPlane(null);
        return;
      }
      setActiveAxis(axis);
      applyPlane({
        axis,
        position,
        flip,
        customNormal: customNormal ?? undefined,
      });
    },
    [activeAxis, applyPlane, position, flip, customNormal],
  );

  const handlePositionChange = useCallback(
    (value: number) => {
      const next = clampClipPosition(value);
      setPosition(next);
      if (!activeAxis) return;
      applyPlane({
        axis: activeAxis,
        position: next,
        flip,
        customNormal: customNormal ?? undefined,
      });
    },
    [activeAxis, applyPlane, flip, customNormal],
  );

  const handleFlipChange = useCallback(
    (value: boolean) => {
      setFlip(value);
      if (!activeAxis) return;
      applyPlane({
        axis: activeAxis,
        position,
        flip: value,
        customNormal: customNormal ?? undefined,
      });
    },
    [activeAxis, applyPlane, position, customNormal],
  );

  const handleNormalInputChange = useCallback((raw: string) => {
    setNormalInput(raw);
    if (!raw.trim()) {
      setNormalParseError(false);
      return;
    }
    const parsed = parseNormalInput(raw);
    setNormalParseError(parsed === null);
  }, []);

  const handleNormalApply = useCallback(() => {
    if (!activeAxis) return;
    if (normalInput.trim() && !customNormal) {
      setNormalParseError(true);
      return;
    }
    applyPlane({
      axis: activeAxis,
      position,
      flip,
      customNormal: customNormal ?? undefined,
    });
  }, [activeAxis, applyPlane, position, flip, customNormal, normalInput]);

  const handleNormalReset = useCallback(() => {
    setNormalInput('');
    setNormalParseError(false);
    if (!activeAxis) return;
    applyPlane({ axis: activeAxis, position, flip });
  }, [activeAxis, applyPlane, position, flip]);

  const handleClearAll = useCallback(() => {
    setActiveAxis(null);
    setPosition(DEFAULT_CLIP_POSITION);
    setFlip(false);
    setClipBox(null);
    adapter?.setSectionPlane(null);
    adapter?.setClippingBox(null);
  }, [adapter]);

  const handleEnableClipBox = useCallback(() => {
    setClipBox(DEFAULT_CLIPPING_BOX);
    adapter?.setClippingBox(DEFAULT_CLIPPING_BOX);
  }, [adapter]);

  const handleClipBoxChange = useCallback(
    (next: ClippingBoxSpec) => {
      setClipBox(next);
      adapter?.setClippingBox(next);
    },
    [adapter],
  );

  const handleTightenToSelection = useCallback(() => {
    const selection = adapter?.getSelectionBounds?.();
    const model = adapter?.getModelBounds() ?? null;
    if (!selection || !model) return;
    const next = tightenBoxFromBounds(selection, model);
    if (!next) return;
    setClipBox(next);
    adapter?.setClippingBox(next);
  }, [adapter]);

  const handleClipBoxClear = useCallback(() => {
    setClipBox(null);
    adapter?.setClippingBox(null);
  }, [adapter]);

  if (!visible) return null;

  return (
    <section
      aria-label="Section and clip tool card"
      data-testid={testId ?? 'section-and-clip-tool-card'}
      style={containerStyle}
    >
      <header style={headerStyle}>
        <div style={titleStyle}>
          <Icon name="crop" size={24} decorative />
          <span>Section and clip</span>
        </div>
        {onClose ? (
          <IconButton
            aria-label="Close section and clip tool card"
            onClick={onClose}
          >
            <Icon name="close" size={20} decorative />
          </IconButton>
        ) : null}
      </header>

      <div style={subtitleStyle}>
        Trim the model along an axis or a custom plane normal. Use the
        clipping box to focus on a tight region.
      </div>

      <SectionTitle icon="crop" label="Section plane" />
      <div role="group" aria-label="Section plane axis" style={chipRowStyle}>
        {AXIS_OPTIONS.map((option) => {
          const active = activeAxis === option.axis;
          return (
            <button
              key={option.axis}
              type="button"
              onClick={() => handleAxisToggle(option.axis)}
              aria-pressed={active}
              aria-label={
                active
                  ? `Disable ${option.accessibleLabel}`
                  : `Enable ${option.accessibleLabel}`
              }
              data-testid={`section-clip-axis-${option.axis}`}
              style={axisChipStyle(active, !adapter)}
              disabled={!adapter}
            >
              <Icon name="crop" size={20} decorative />
              <span>{option.label}</span>
            </button>
          );
        })}
      </div>

      {activeAxis ? (
        <>
          <div role="group" aria-label="Section plane offset" style={sliderRowStyle}>
            <label htmlFor="section-clip-position" style={sliderLabelStyle}>
              Position
            </label>
            <input
              id="section-clip-position"
              type="range"
              min={0}
              max={1}
              step={0.01}
              value={position}
              onChange={(e) => handlePositionChange(Number(e.currentTarget.value))}
              aria-valuemin={0}
              aria-valuemax={1}
              aria-valuenow={position}
              aria-valuetext={`${Math.round(position * 100)} percent`}
              data-testid="section-clip-position"
              style={sliderInputStyle}
            />
            <output
              htmlFor="section-clip-position"
              style={positionReadoutStyle}
              aria-live="polite"
            >
              {Math.round(position * 100)}%
            </output>
          </div>

          <label style={flipRowStyle}>
            <input
              type="checkbox"
              checked={flip}
              onChange={(e) => handleFlipChange(e.currentTarget.checked)}
              aria-label="Flip plane direction"
              data-testid="section-clip-flip"
            />
            <Icon name="flip" size={20} decorative />
            <span>Flip plane direction</span>
          </label>

          <SectionTitle icon="straighten" label="Custom plane normal" />
          <div style={normalEditorStyle}>
            <label htmlFor="section-clip-normal" style={sliderLabelStyle}>
              Normal vector (x, y, z)
            </label>
            <input
              id="section-clip-normal"
              type="text"
              value={normalInput}
              onChange={(e) => handleNormalInputChange(e.currentTarget.value)}
              placeholder="0, 0, 1"
              spellCheck={false}
              autoComplete="off"
              aria-invalid={normalParseError}
              aria-label="Custom plane normal vector"
              data-testid="section-clip-normal"
              style={normalInputStyle}
            />
            {customNormal ? (
              <span style={normalPreviewStyle} data-testid="section-clip-normal-preview">
                {formatNormalInput(customNormal)}
              </span>
            ) : null}
            <div style={normalActionsStyle}>
              <Button
                variant="ghost"
                onClick={handleNormalReset}
                aria-label="Reset to axis-aligned normal"
                data-testid="section-clip-normal-reset"
              >
                <Icon name="restart_alt" size={20} decorative />
                <span>Reset axis</span>
              </Button>
              <Button
                variant="secondary"
                onClick={handleNormalApply}
                disabled={!adapter || (!!normalInput.trim() && !customNormal)}
                aria-label="Apply custom plane normal"
                data-testid="section-clip-normal-apply"
              >
                <Icon name="straighten" size={20} decorative />
                <span>Apply normal</span>
              </Button>
            </div>
            {normalParseError ? (
              <Banner
                severity="error"
                title="Invalid normal vector"
                body={
                  <span>
                    Use three comma- or space-separated numbers
                    (e.g. <code style={inlineMonoStyle}>1, 0, 0</code>).
                    Zero-length vectors are rejected.
                  </span>
                }
                narrationKey="section-clip-normal-error"
                data-testid="section-clip-normal-error"
              />
            ) : null}
          </div>
        </>
      ) : null}

      <SectionTitle icon="crop_free" label="Clipping box" />
      {clipBox ? (
        <ClippingBoxEditor
          box={clipBox}
          adapterPresent={!!adapter}
          onChange={handleClipBoxChange}
          onTighten={handleTightenToSelection}
          onClear={handleClipBoxClear}
          tightenAvailable={!!adapter?.getSelectionBounds}
        />
      ) : (
        <div style={chipRowStyle}>
          <Button
            variant="secondary"
            onClick={handleEnableClipBox}
            disabled={!adapter}
            aria-label="Enable clipping box"
            data-testid="section-clip-box-enable"
          >
            <Icon name="crop_free" size={20} decorative />
            <span>Enable clipping box</span>
          </Button>
        </div>
      )}

      <footer style={footerStyle}>
        <Button
          variant="ghost"
          onClick={handleClearAll}
          aria-label="Reset all section and clipping state"
          data-testid="section-clip-reset-all"
        >
          <Icon name="restart_alt" size={20} decorative />
          <span>Reset all</span>
        </Button>
      </footer>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Helpers (pure) — exported for test-driven verification
// ---------------------------------------------------------------------------

export function tightenBoxFromBounds(
  selection: ModelBounds,
  model: ModelBounds,
): ClippingBoxSpec | null {
  const [scx, scy, scz] = selection.center;
  const [ssx, ssy, ssz] = selection.size;
  const [mcx, mcy, mcz] = model.center;
  const [msx, msy, msz] = model.size;
  if (msx <= 0 || msy <= 0 || msz <= 0) return null;
  const fracMin = (sCenter: number, sSize: number, mCenter: number, mSize: number): number =>
    clampClipPosition(((sCenter - sSize / 2) - (mCenter - mSize / 2)) / mSize);
  const fracMax = (sCenter: number, sSize: number, mCenter: number, mSize: number): number =>
    clampClipPosition(((sCenter + sSize / 2) - (mCenter - mSize / 2)) / mSize);
  return {
    xMin: fracMin(scx, ssx, mcx, msx),
    xMax: fracMax(scx, ssx, mcx, msx),
    yMin: fracMin(scy, ssy, mcy, msy),
    yMax: fracMax(scy, ssy, mcy, msy),
    zMin: fracMin(scz, ssz, mcz, msz),
    zMax: fracMax(scz, ssz, mcz, msz),
  };
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function SectionTitle({ icon, label }: { icon: IconName; label: string }) {
  return (
    <div role="separator" aria-label={label} style={sectionTitleStyle}>
      <Icon name={icon} size={20} decorative />
      <span>{label}</span>
    </div>
  );
}

function ClippingBoxEditor({
  box,
  adapterPresent,
  onChange,
  onTighten,
  onClear,
  tightenAvailable,
}: {
  box: ClippingBoxSpec;
  adapterPresent: boolean;
  onChange: (next: ClippingBoxSpec) => void;
  onTighten: () => void;
  onClear: () => void;
  tightenAvailable: boolean;
}) {
  const updateField = (field: keyof ClippingBoxSpec, value: number) => {
    const draft: Record<keyof ClippingBoxSpec, number> = {
      xMin: box.xMin,
      xMax: box.xMax,
      yMin: box.yMin,
      yMax: box.yMax,
      zMin: box.zMin,
      zMax: box.zMax,
    };
    draft[field] = clampClipPosition(value);
    if (draft.xMin > draft.xMax) draft.xMin = draft.xMax;
    if (draft.yMin > draft.yMax) draft.yMin = draft.yMax;
    if (draft.zMin > draft.zMax) draft.zMin = draft.zMax;
    onChange(draft);
  };

  return (
    <div style={clipBoxEditorStyle}>
      {(['x', 'y', 'z'] as const).map((axis) => (
        <div key={axis} style={clipBoxAxisRowStyle}>
          <span style={clipBoxAxisLabelStyle}>{axis.toUpperCase()}</span>
          <input
            type="range"
            min={0}
            max={1}
            step={0.01}
            value={box[`${axis}Min` as keyof ClippingBoxSpec]}
            onChange={(e) => updateField(`${axis}Min` as keyof ClippingBoxSpec, Number(e.currentTarget.value))}
            aria-label={`${axis.toUpperCase()} axis minimum`}
            data-testid={`section-clip-box-${axis}-min`}
          />
          <input
            type="range"
            min={0}
            max={1}
            step={0.01}
            value={box[`${axis}Max` as keyof ClippingBoxSpec]}
            onChange={(e) => updateField(`${axis}Max` as keyof ClippingBoxSpec, Number(e.currentTarget.value))}
            aria-label={`${axis.toUpperCase()} axis maximum`}
            data-testid={`section-clip-box-${axis}-max`}
          />
        </div>
      ))}
      <div style={clipBoxActionsStyle}>
        <Button
          variant="ghost"
          onClick={onTighten}
          disabled={!adapterPresent || !tightenAvailable}
          aria-label="Tighten clipping box to selection bounds"
          data-testid="section-clip-box-tighten"
        >
          <Icon name="filter_center_focus" size={20} decorative />
          <span>Tighten to selection</span>
        </Button>
        <Button
          variant="secondary"
          onClick={onClear}
          aria-label="Disable clipping box"
          data-testid="section-clip-box-clear"
        >
          <Icon name="close" size={20} decorative />
          <span>Disable</span>
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Styles (token-only)
// ---------------------------------------------------------------------------

const containerStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: tokens.space[3],
  background: tokens.color.surface[2],
  border: tokens.border.thin,
  borderRadius: tokens.radius.lg,
  padding: tokens.space[3],
  width: tokens.width.panelMd,
  boxShadow: tokens.elevation[1],
};

const headerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: tokens.space[2],
};

const titleStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: tokens.space[2],
  color: tokens.color.ink.primary,
  fontFamily: tokens.font.family.ui,
  fontSize: tokens.font.size.lg,
  fontWeight: tokens.font.weight.semibold,
};

const subtitleStyle: CSSProperties = {
  color: tokens.color.ink.secondary,
  fontFamily: tokens.font.family.ui,
  fontSize: tokens.font.role.body.size,
};

const sectionTitleStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: tokens.space[2],
  color: tokens.color.ink.secondary,
  fontFamily: tokens.font.family.ui,
  fontSize: tokens.font.size.sm,
  fontWeight: tokens.font.weight.medium,
  textTransform: 'uppercase',
  letterSpacing: tokens.border.width.thin,
  borderTop: tokens.border.thin,
  paddingTop: tokens.space[2],
};

const chipRowStyle: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: tokens.space[1],
  alignItems: 'center',
};

function axisChipStyle(active: boolean, disabled: boolean): CSSProperties {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.space[1],
    minHeight: tokens.density.touchMin,
    padding: `${tokens.space[1]}px ${tokens.space[3]}px`,
    borderRadius: tokens.radius.chip,
    border: `${tokens.border.width.thin}px solid ${tokens.color.surface[3]}`,
    background: active
      ? tokens.color.accent.subtle
      : disabled
        ? tokens.color.surface[2]
        : tokens.color.surface[1],
    color: disabled ? tokens.color.ink.muted : tokens.color.ink.primary,
    fontFamily: tokens.font.family.ui,
    fontSize: tokens.font.role.label.size,
    fontWeight: active ? tokens.font.weight.medium : tokens.font.weight.regular,
    cursor: disabled ? 'not-allowed' : 'pointer',
  };
}

const sliderRowStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'auto 1fr auto',
  alignItems: 'center',
  gap: tokens.space[2],
  minHeight: tokens.density.touchMin,
};

const sliderInputStyle: CSSProperties = {
  width: '100%',
  accentColor: tokens.color.accent.primary,
  minHeight: tokens.density.touchMin,
};

const sliderLabelStyle: CSSProperties = {
  fontFamily: tokens.font.family.ui,
  fontSize: tokens.font.size.sm,
  color: tokens.color.ink.secondary,
};

const positionReadoutStyle: CSSProperties = {
  fontFamily: tokens.font.family.mono,
  fontSize: tokens.font.size.sm,
  color: tokens.color.ink.primary,
  minWidth: tokens.layout.profileToolbarHeight,
  textAlign: 'right',
};

const flipRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: tokens.space[2],
  color: tokens.color.ink.secondary,
  fontFamily: tokens.font.family.ui,
  fontSize: tokens.font.size.sm,
  minHeight: tokens.density.touchMin,
  cursor: 'pointer',
};

const normalEditorStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: tokens.space[1],
};

const normalInputStyle: CSSProperties = {
  width: '100%',
  minHeight: tokens.density.touchMin,
  padding: `${tokens.space[1]}px ${tokens.space[2]}px`,
  fontFamily: tokens.font.family.mono,
  fontSize: tokens.font.size.sm,
  background: tokens.color.surface[1],
  color: tokens.color.ink.primary,
  border: tokens.border.thin,
  borderRadius: tokens.radius.input,
};

const normalPreviewStyle: CSSProperties = {
  fontFamily: tokens.font.family.mono,
  fontSize: tokens.font.size.xs,
  color: tokens.color.ink.muted,
};

const normalActionsStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: tokens.space[2],
  flexWrap: 'wrap',
};

const inlineMonoStyle: CSSProperties = {
  fontFamily: tokens.font.family.mono,
  fontSize: tokens.font.size.xs,
};

const clipBoxEditorStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: tokens.space[2],
  border: tokens.border.thin,
  borderRadius: tokens.radius.md,
  background: tokens.color.surface[1],
  padding: tokens.space[2],
};

const clipBoxAxisRowStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'auto 1fr 1fr',
  alignItems: 'center',
  gap: tokens.space[2],
  minHeight: tokens.density.touchMin,
};

const clipBoxAxisLabelStyle: CSSProperties = {
  fontFamily: tokens.font.family.mono,
  fontSize: tokens.font.size.sm,
  color: tokens.color.ink.primary,
  width: tokens.density.rowDense,
};

const clipBoxActionsStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: tokens.space[2],
  flexWrap: 'wrap',
  borderTop: tokens.border.thin,
  paddingTop: tokens.space[2],
};

const footerStyle: CSSProperties = {
  display: 'flex',
  gap: tokens.space[2],
  alignItems: 'center',
  justifyContent: 'flex-end',
  flexWrap: 'wrap',
};
